import { defineMessages, MessageDescriptor } from 'react-intl';
import {
  updateProductSubscriptionMutation_updateProductRenewal_UpdateProductRenewalError,
  UpdateProductRenewalErrorCode
} from '../../core-types';

export const profileSection = defineMessages({
  formHeading: {
    id: 'Profile.profileSection.formHeading',
    defaultMessage: 'More about you'
  },
  withoutSubscription: {
    id: 'profile.noSubscriptionTitle',
    defaultMessage: `You don't have a valid subscription yet.`
  },
  cancelSubscription: {
    id: 'cancel_subscription',
    defaultMessage: 'Cancel subscription'
  },
  cancelSubscriptionTitle: {
    id: 'cancel_subscription_confirmation_title',
    defaultMessage: 'Are you sure you want to cancel your subscription?'
  },
  cancelSubscriptionConfirmation: {
    id: 'cancel_subscription_confirmation_description',
    defaultMessage:
      'Cancellation will be immediate and you will need to activate another subscription to be able to use the service again.'
  },
  cancelSubscriptionSuccess: {
    id: 'cancel_subscription_success',
    defaultMessage: 'Subscription cancelled'
  },
  redeemGiftCardLink: {
    id: 'redeem_a_voucher_code',
    defaultMessage: 'Redeem a voucher code'
  },
  profileUpdated: {
    id: 'profileUpdated',
    defaultMessage: 'Profile updated'
  }
});

export const sections = defineMessages({
  subscription: {
    id: 'subscription',
    defaultMessage: 'Subscription'
  },
  'personal-info': {
    id: 'Profile.sections.personalInfo',
    defaultMessage: 'About you'
  },
  tripHistory: {
    id: 'trip_history',
    defaultMessage: 'Trip history'
  },
  receipts: {
    id: 'receipts',
    defaultMessage: 'Receipts'
  },
  invoices: {
    id: 'invoices',
    defaultMessage: 'Invoices'
  },
  'credit-cards': {
    id: 'Profile.sections.creditCards',
    defaultMessage: 'Payment cards'
  },
  wallet: {
    id: 'modules.Profile.Wallet.title',
    defaultMessage: 'Wallet'
  },
  'pin-code': {
    id: 'pin_code',
    defaultMessage: 'PIN code'
  },
  'your-data': {
    id: 'your_data',
    defaultMessage: 'Your data'
  },
  'assign-rfid-card': {
    id: 'Profile.sections.assignRfidCard',
    defaultMessage: 'Assign RFID card'
  }
});

export const userFormValidation = defineMessages({
  birthYear: {
    id: 'Profile.UserForm.birthYear',
    defaultMessage: 'Please enter a 4 digit birth year, eg 1986'
  },
  email: {
    id: 'Onboarding.EmailForm.emailRequired',
    defaultMessage: 'A valid email is required.'
  }
});

export const userFields = defineMessages({
  name: {
    id: 'Profile.UserForm.Fields.name',
    defaultMessage: 'Name'
  },
  gender: {
    id: 'Profile.UserForm.Fields.gender',
    defaultMessage: 'Gender'
  },
  birthYear: {
    id: 'Profile.UserForm.Fields.birthYear',
    defaultMessage: 'Birth year'
  },
  phoneNumber: {
    id: 'phone_number',
    defaultMessage: 'Phone number'
  },
  email: {
    id: 'email',
    defaultMessage: 'Email'
  },
  agreedToReceiveEmail: {
    id: 'agreedToReceiveEmail',
    defaultMessage: 'Receive information about the scheme, new stations etc.'
  },
  agreedToReceiveExternalPromotions: {
    id: 'agreedToReceiveExternalPromotions',
    defaultMessage:
      'Receive special offers and information from trusted partners, sponsors, the city etc.'
  },
  postalCode: {
    id: 'postal_code',
    defaultMessage: 'Postal code'
  },
  disclaimer: {
    id: 'Profile.UserForm.Fields.disclaimer',
    defaultMessage:
      'The following information is optional. While we appreciate knowing more about our customers, you may edit or remove your answers at any time.'
  },
  nameDescription: {
    id: 'Profile.UserForm.Fields.nameDescription',
    defaultMessage: "Used when we're communicating with you"
  }
});

export const phoneNumberForm = defineMessages({
  heading: {
    id: 'Profile.PhoneNumberForm.heading',
    defaultMessage: 'What we need to know'
  },
  isEditing: {
    id: 'Profile.PhoneNumberForm.isEditing',
    defaultMessage: 'Enter your new phone number'
  },
  disclaimer: {
    id: 'Profile.PhoneNumberForm.disclaimer',
    defaultMessage:
      'Your phone number is required to use our bikes. The number is used to log you in, and to contact you if something goes wrong'
  },
  updated: {
    id: 'Profile.PhoneNumberForm.updated',
    defaultMessage: 'Phone number updated'
  }
});

export const renewalMessages = defineMessages({
  confirmCancel: {
    id: 'renewal_disabling_confirmation_title',
    defaultMessage: 'Are you sure you want to cancel auto-renewal?'
  },
  confirmCancelWithValidSubscription: {
    id: 'subscription_still_active_until',
    defaultMessage: 'Current subscription will still be active until {validTo}'
  },
  subscriptionStillPending: {
    id: 'subscription_still_pending',
    defaultMessage: 'Current subscription will still be pending for activation'
  },
  confirmRenew: {
    id: 'renewal_enabling_confirmation_title',
    defaultMessage: 'Are you sure you want to renew subscription automatically?'
  },
  subscriptionRenewalOnDate: {
    id: 'renewal_next_scheduled',
    defaultMessage: 'The next renewal will be for {price} on {date}'
  }
});

const updateProductRenewalMessages = defineMessages({
  ERR_PRODUCT_NOT_RENEWABLE: {
    id: 'productIsNotRenewableError',
    defaultMessage: 'Product is not renewable'
  },
  ERR_USER_NO_SUBSCRIPTION: {
    id: 'userNoSubscriptionError',
    defaultMessage: "You don't have an active subscription."
  }
});

export function getUpdateProductRenewalErrorMessage(
  error: updateProductSubscriptionMutation_updateProductRenewal_UpdateProductRenewalError
): MessageDescriptor {
  switch (error.errorCode) {
    case UpdateProductRenewalErrorCode.ERR_PRODUCT_NOT_RENEWABLE: {
      return updateProductRenewalMessages.ERR_PRODUCT_NOT_RENEWABLE;
    }
    case UpdateProductRenewalErrorCode.ERR_USER_NO_SUBSCRIPTION: {
      return updateProductRenewalMessages.ERR_USER_NO_SUBSCRIPTION;
    }
  }
}
